import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { Bookmark } from "lucide-react";
import { toast } from "sonner";
import { domainsPage, type DomainFilters } from "@/api/domains";
import { createFilterSource } from "@/api/run-sources";
import type { DomainSummary } from "@/api/types";
import { Page } from "@/components/page";
import { DataTable } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { RelativeTime } from "@/components/relative-time";
import { EmptyState } from "@/components/empty-state";
import { UrlSearch } from "@/components/url-search";
import { RunPicker } from "@/components/run-picker";
import { TagPicker } from "@/components/tag-picker";
import { BulkActionBar } from "@/components/bulk-action-bar";
import { useUrlFilters } from "@/lib/use-filters";

const STATUSES = [
  "pending",
  "extracted",
  "skipped",
  "unreachable",
  "error",
  "ignored",
] as const;

// Codes the SPA exposes in the reason multi-select. Mirrors
// `UnreachableReason` in `src/leadsniffer/enums.py`. `_heavy`-suffixed
// variants are entered by appending "_heavy" to the base code; the
// quick-pick groups below cover the common selections.
const REASON_QUICK_PICKS: Array<{
  key: string;
  label: string;
  codes: string[];
}> = [
  {
    key: "filtered",
    label: "Filtered-style (recoverable)",
    codes: [
      "http_403",
      "http_429",
      "tls_handshake",
      "tcp_timeout",
      "tcp_reset",
      "too_many_redirects",
    ],
  },
  {
    key: "heavy_failed",
    label: "Heavy pass also failed",
    codes: [
      "http_403_heavy",
      "http_429_heavy",
      "tls_handshake_heavy",
      "tcp_timeout_heavy",
      "tcp_reset_heavy",
      "too_many_redirects_heavy",
    ],
  },
];

const REASON_CODES = [
  "dns",
  "tcp_refused",
  "tcp_timeout",
  "tcp_reset",
  "tls_handshake",
  "read_timeout",
  "wall_timeout",
  "chunked_encoding",
  "content_decoding",
  "http_403",
  "http_429",
  "http_404",
  "http_4xx_other",
  "http_5xx",
  "too_many_redirects",
  "too_large",
  "bad_url",
  "idn_encoding",
  "unknown",
] as const;

type DomainsSearch = {
  status?: string;
  tag?: string;
  run_id?: string;
  q?: string;
  unreachable_reason?: string;
} & Record<string, string | undefined>;

export function DomainsListPage() {
  const [filters, setFilters] = useUrlFilters<DomainsSearch>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const qc = useQueryClient();
  const navigate = useNavigate();

  // URL encodes multi-tag as comma-separated `tag=a,b`; useUrlFilters
  // collapses repeated keys to the last value, so a single string is the
  // only shape that survives a round-trip through the address bar.
  // Same trick reused for `unreachable_reason`.
  const tagList = useMemo<string[]>(
    () => (filters.tag ? filters.tag.split(",").filter(Boolean) : []),
    [filters.tag],
  );
  const reasonList = useMemo<string[]>(
    () =>
      filters.unreachable_reason
        ? filters.unreachable_reason.split(",").filter(Boolean)
        : [],
    [filters.unreachable_reason],
  );

  const filterArg: DomainFilters = {
    status: filters.status,
    tag: tagList.length ? tagList : undefined,
    run_id: filters.run_id,
    q: filters.q,
    unreachable_reason: reasonList.length ? reasonList : undefined,
    limit: 100,
  };

  const anyFilterActive = Boolean(
    filters.status ||
      filters.tag ||
      filters.run_id ||
      filters.q ||
      filters.unreachable_reason,
  );

  const saveAsSource = useMutation({
    mutationFn: (name: string) =>
      createFilterSource(name, {
        status: filters.status || undefined,
        tag: tagList.length ? tagList : undefined,
        run_id: filters.run_id || undefined,
        q: filters.q || undefined,
        unreachable_reason: reasonList.length ? reasonList : undefined,
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["run-sources"] });
      toast.success(
        `Saved "${created.name}" (${created.line_count.toLocaleString()} ` +
          `matching domains). Run it from /runs.`,
        {
          action: {
            label: "Go to runs",
            onClick: () => navigate({ to: "/runs" }),
          },
        },
      );
    },
    onError: (err) => {
      toast.error(`Save failed: ${(err as Error).message}`);
    },
  });

  const onSaveFilterClick = () => {
    if (!anyFilterActive) return;
    const suggested = describeFilter({
      status: filters.status,
      tags: tagList,
      runId: filters.run_id,
      q: filters.q,
      reasons: reasonList,
    });
    const name = window.prompt(
      "Name this filter source — runs launched from it stream every " +
        "domain matching the filter at run time.",
      suggested,
    );
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Name required");
      return;
    }
    saveAsSource.mutate(trimmed);
  };

  const query = useInfiniteQuery({
    queryKey: ["domains", filterArg],
    queryFn: ({ pageParam }) => domainsPage(pageParam ?? null, filterArg),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });

  const columns = useMemo<ColumnDef<DomainSummary, unknown>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            className="cursor-pointer"
            checked={table.getIsAllRowsSelected()}
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomeRowsSelected();
            }}
            onChange={table.getToggleAllRowsSelectedHandler()}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="cursor-pointer"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            aria-label="Select row"
          />
        ),
        size: 32,
      },
      {
        id: "url",
        header: "URL",
        cell: ({ row }) => (
          <Link
            to="/domains/$domainId"
            params={{ domainId: row.original.id }}
            className="font-mono text-xs text-accent hover:underline"
          >
            {row.original.normalized_url}
          </Link>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const reason = row.original.unreachable_reason;
          const tooltip = [row.original.init_error, reason]
            .filter(Boolean)
            .join(" — ");
          return (
            <div className="flex items-center gap-1.5">
              <StatusBadge
                status={row.original.status}
                title={tooltip || undefined}
              />
              {row.original.status === "unreachable" && reason && (
                <span
                  className="badge text-[10px] font-mono"
                  title={tooltip || undefined}
                >
                  {reason}
                </span>
              )}
            </div>
          );
        },
        size: 110,
      },
      {
        id: "tags",
        header: "Tags",
        cell: ({ row }) =>
          row.original.tags.length === 0 ? (
            <span className="text-muted">—</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {row.original.tags.map((t) => (
                <span key={t} className="badge text-[10px]">{t}</span>
              ))}
            </div>
          ),
      },
      {
        id: "last_scanned",
        header: "Last scanned",
        cell: ({ row }) => (
          <span className="text-xs text-muted">
            <RelativeTime iso={row.original.last_scanned_at} />
          </span>
        ),
        size: 160,
      },
    ],
    [],
  );

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  return (
    <Page title="Domains">
      <section className="card p-3 flex flex-wrap items-end gap-3">
        <UrlSearch
          value={filters.q ?? ""}
          onChange={(q) => setFilters({ q })}
        />
        <div>
          <label className="label">Status</label>
          <select
            className="input w-40"
            value={filters.status ?? ""}
            onChange={(e) => setFilters({ status: e.target.value || undefined })}
          >
            <option value="">Any</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Tag</label>
          <TagPicker
            value={tagList}
            onChange={(next) =>
              setFilters({ tag: next.length ? next.join(",") : undefined })
            }
          />
        </div>
        <div>
          <label className="label">Run</label>
          <RunPicker
            value={filters.run_id}
            onChange={(run_id) => setFilters({ run_id })}
          />
        </div>
        {filters.status === "unreachable" ? (
          <ReasonPicker
            value={reasonList}
            onChange={(next) =>
              setFilters({
                unreachable_reason: next.length ? next.join(",") : undefined,
              })
            }
          />
        ) : null}
        {anyFilterActive ? (
          <>
            <button
              className="btn-ghost text-xs"
              onClick={onSaveFilterClick}
              disabled={saveAsSource.isPending}
              title="Save this filter as a re-runnable virtual source. The filter is saved (not the matching domains), so new domains that match the filter later are included in future runs."
            >
              <Bookmark className="h-3.5 w-3.5" />
              {saveAsSource.isPending ? "Saving…" : "Save as source"}
            </button>
            <button
              className="btn-ghost text-xs"
              onClick={() =>
                setFilters({
                  status: undefined,
                  tag: undefined,
                  run_id: undefined,
                  q: undefined,
                  unreachable_reason: undefined,
                })
              }
            >
              Clear filters
            </button>
          </>
        ) : null}
      </section>

      <section className="card overflow-hidden">
        <DataTable
          columns={columns}
          query={query}
          rowId={(d) => d.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          emptyState={<EmptyState title="No domains match" hint="Try clearing filters or searching by URL prefix." />}
        />
      </section>

      <BulkActionBar
        selectedIds={selectedIds}
        filter={filterArg}
        onClearSelection={() => setRowSelection({})}
      />
    </Page>
  );
}

function ReasonPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = new Set(value);
  const summary = value.length === 0
    ? "Any reason"
    : value.length === 1
      ? value[0]
      : `${value.length} reasons`;

  const toggle = (code: string) => {
    const next = new Set(value);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(Array.from(next));
  };

  const applyQuickPick = (codes: string[]) => onChange(codes);
  const clear = () => onChange([]);

  return (
    <div className="relative">
      <label className="label">Reason</label>
      <button
        type="button"
        className="input w-48 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {summary}
      </button>
      {open ? (
        <div
          className="absolute z-10 mt-1 w-72 rounded-md border border-border bg-card p-3 shadow-md"
          // Click-outside is intentionally minimal: a second toggle on the
          // trigger or a click on Apply closes the panel.
        >
          <div className="mb-2 text-xs font-medium text-muted">Quick picks</div>
          {REASON_QUICK_PICKS.map((qp) => (
            <button
              key={qp.key}
              type="button"
              className="btn-ghost mb-1 block w-full text-left text-xs"
              onClick={() => applyQuickPick(qp.codes)}
            >
              {qp.label}
            </button>
          ))}
          <hr className="my-2 border-border" />
          <div className="mb-2 text-xs font-medium text-muted">Individual codes</div>
          <div className="max-h-56 overflow-auto pr-1">
            {REASON_CODES.map((code) => (
              <label
                key={code}
                className="flex cursor-pointer items-center gap-2 py-0.5 text-xs"
              >
                <input
                  type="checkbox"
                  checked={selected.has(code)}
                  onChange={() => toggle(code)}
                />
                <span className="font-mono">{code}</span>
              </label>
            ))}
          </div>
          <div className="mt-2 flex justify-between">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={clear}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}


function describeFilter(parts: {
  status?: string;
  tags: string[];
  runId?: string;
  q?: string;
  reasons: string[];
}): string {
  const bits: string[] = [];
  if (parts.status) bits.push(parts.status);
  if (parts.tags.length) bits.push(parts.tags.join("+"));
  if (parts.q) bits.push(`q:${parts.q}`);
  // run_id is an opaque ObjectId; truncate so the suggested name stays
  // human-readable but still unique enough to disambiguate.
  if (parts.runId) bits.push(`run-${parts.runId.slice(0, 6)}`);
  if (parts.reasons.length) bits.push(parts.reasons.join("+"));
  return bits.join(" · ") || "filter";
}
